'use client';

import { API_RESPONSE_CODE } from '@/constants';
import { getTermConsentPending } from '@/features/term/api/getTermConsentPending';
import RequiredConsent from '@/features/term/components/RequiredConsent/RequiredConsent';
import type { PendingReconsentTerm } from '@monorepo/common';
import { useSession } from 'next-auth/react';
import { useCallback, useRef, useState } from 'react';

/**
 * 表示層②（購入導線の再喚起）とサーバー起点フォールバックのガード。
 *
 * - `runWithConsent`: くじ引き・コイン購入の遷移前に未同意を判定し、あればモーダルを出して中断する。
 *   訪問時モーダルの24時間抑制に関わらず必ず表示する。
 * - `handleApiError`: くじ/購入 API から再同意要求の 400 を受けた場合にもモーダルを喚起する
 *   （直リンクで確認画面に到達した場合など、遷移前の判定をすり抜けた経路のフォールバック）。
 * - `reconsentModal`: 呼び出し側の JSX に差し込む。
 */
export const useReconsentGuard = () => {
  const [terms, setTerms] = useState<PendingReconsentTerm[]>([]);
  // 同意時の抑制解除をユーザーごとに行うために使う。
  // 本フックはユーザー操作の後に動くため、この時点でセッションは解決済み
  const { data: sessionData } = useSession();
  const userId = sessionData?.user?.id;
  // 同意完了後に再開する処理。モーダル表示中に差し替わらないよう ref で保持する
  const pendingActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const fetchPendingTerms = useCallback(async (): Promise<PendingReconsentTerm[]> => {
    try {
      const { result } = await getTermConsentPending();
      return result.documents ?? [];
    } catch {
      // 判定できない場合は通す。サーバー側ミドルウェアが最終的にブロックする
      return [];
    }
  }, []);

  /**
   * 未同意なら遷移を中断してモーダルを出す。同意が完了したら action を実行する。
   * 同意済み・判定不能の場合はその場で action を実行する。
   */
  const runWithConsent = useCallback(
    async (action: () => void | Promise<void>) => {
      const pending = await fetchPendingTerms();
      if (pending.length === 0) {
        await action();
        return;
      }
      pendingActionRef.current = action;
      setTerms(pending);
    },
    [fetchPendingTerms],
  );

  /**
   * API エラーが再同意要求（code=4）ならモーダルを喚起する。
   * @returns このエラーを本フックで処理した場合 true。呼び出し側は true のとき独自のエラー表示を行わない
   */
  const handleApiError = useCallback(
    async (error: unknown): Promise<boolean> => {
      const code = (error as { code?: number } | null)?.code;
      if (code !== API_RESPONSE_CODE.termReconsentRequired.value) return false;

      const pending = await fetchPendingTerms();
      if (pending.length === 0) return false;

      pendingActionRef.current = null;
      setTerms(pending);
      return true;
    },
    [fetchPendingTerms],
  );

  const handleFinished = useCallback((isAllAgreed: boolean) => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setTerms([]);
    // 全文書に同意した場合のみ、中断していた遷移を続行する
    if (isAllAgreed && action) void action();
  }, []);

  // 購入導線（表示層②）は訪問時モーダルの抑制対象外。ここでのキャンセルで
  // 抑制時刻を更新すると、購入ボタンを押してキャンセルするたびに層①のタイマーが
  // 延び、訪問時モーダルが出なくなるため suppressOnCancel は false にする
  const reconsentModal =
    terms.length > 0 ? (
      <RequiredConsent userId={userId} terms={terms} onFinished={handleFinished} suppressOnCancel={false} />
    ) : null;

  return { runWithConsent, handleApiError, reconsentModal };
};
